(() => {
  if (customElements.get('ap-scent-upsell-picker')) return;

  class APScentUpsellPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === '1') return;
      this.dataset.ready = '1';
      this.inputs = [...this.querySelectorAll('[data-ap-scent]')];
      this.filters = [...this.querySelectorAll('[data-ap-scent-filter]')];
      this.rail = this.querySelector('.ap-df__scents-rail');
      this.thumb = this.querySelector('[data-ap-scent-scroll-thumb]');
      this.status = this.querySelector('[data-ap-scent-status]');
      this.math = this.querySelector('[data-ap-scent-math]');
      this.deal = this.querySelector('[data-ap-scent-deal]');
      this.size = '30ml';
      this.category = 'bestseller';
      this.selection = this.querySelector('[data-ap-scent-selection]');
      this.inputs.forEach((input) => { input.variants = JSON.parse(input.dataset.variants); });
      this.querySelectorAll('[data-ap-scent-size]').forEach((button) => button.addEventListener('click', () => this.selectSize(button.dataset.apScentSize)));
      this.selection?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-scent]');
        if (!button) return;
        const input = this.inputs.find((item) => item.dataset.variantId === button.dataset.removeScent);
        if (input) { input.checked = false; this.render(); this.emitChange(input); }
      });
      this.maxSelected = Number(this.dataset.maxSelected || 7);
      this.tiers = String(this.dataset.tiers || '3:10,5:15,7:20').split(',').map((entry) => {
        const [quantity, percent] = entry.split(':').map(Number);
        return { quantity, percent, rate: percent / 100 };
      });
      this.onResize = () => this.renderScroll();

      this.inputs.forEach((input) => input.addEventListener('change', () => {
        this.render();
        this.emitChange(input);
      }));
      this.filters.forEach((button) => button.addEventListener('click', () => this.selectCategory(button)));
      this.rail?.addEventListener('scroll', this.onResize, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.loadVisibleImages();
      this.render();
      requestAnimationFrame(() => this.renderScroll());
      this.dispatchEvent(new CustomEvent('ap:scent-picker-ready', { bubbles: true }));
      this.loadExtraPacks();
    }

    async loadExtraPacks() {
      const template = this.querySelector('.ap-df__scent--pack');
      if (!template) return;
      const packs = JSON.parse(this.dataset.extraPacks || '[]');
      const root = window.Shopify?.routes?.root || '/';
      const results = await Promise.allSettled(packs.map(async ([handle, name, contents]) => {
        const response = await fetch(`${root}products/${handle}.js`);
        if (!response.ok) throw new Error(`Unable to load ${handle}`);
        const product = await response.json();
        if (!product.available) return null;
        const card = template.cloneNode(true);
        card.hidden = this.category !== 'packs';
        card.classList.remove('is-disabled');
        const input = card.querySelector('[data-ap-scent]');
        input.checked = false;
        input.dataset.name = name;
        input.variants = product.variants.map((variant) => ({ id: variant.id, size: variant.title, price: variant.price, compare: variant.compare_at_price || 0, available: variant.available }));
        input.dataset.variants = JSON.stringify(input.variants);
        card.querySelector('.ap-df__scent-name').textContent = name;
        const list = card.querySelector('.ap-df__scent-contents');
        list.replaceChildren(...contents.split('|').map((text) => { const span = document.createElement('span'); span.textContent = text; return span; }));
        const image = card.querySelector('img');
        image.removeAttribute('srcset');
        image.removeAttribute('data-srcset');
        image.removeAttribute('data-src');
        const imageUrl = new URL(product.featured_image || product.images[0], location.href);
        imageUrl.searchParams.set('width', '320');
        image.src = imageUrl.href;
        image.loading = 'lazy';
        input.addEventListener('change', () => { this.render(); this.emitChange(input); });
        return card;
      }));
      results.forEach((result) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        this.rail.append(result.value);
        this.inputs.push(result.value.querySelector('[data-ap-scent]'));
      });
      this.render();
      if (results.some((result) => result.status === 'rejected')) this.status.textContent = 'Some packs could not load. Refresh to try again.';
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    get selectedInputs() {
      return this.inputs.filter((input) => input.checked);
    }

    get selectedItems() {
      return this.selectedInputs.map((input) => ({
        variantId: Number(input.dataset.variantId),
        name: input.dataset.name,
        price: Number(input.dataset.price || 0),
        size: input.dataset.size,
        pack: input.dataset.pack === 'true',
        input,
      }));
    }

    get selectedOils() {
      return this.selectedInputs.filter((input) => input.dataset.pack !== 'true');
    }

    get discountPercent() {
      return this.matchedTier(this.selectedOils.length)?.percent || 0;
    }

    get discountCents() {
      const rate = this.discountPercent / 100;
      if (!rate) return 0;
      return this.selectedOils.reduce((sum, input) => sum + Math.floor(Number(input.dataset.price || 0) * rate), 0);
    }

    get subtotalCents() {
      return this.selectedInputs.reduce((sum, input) => sum + Number(input.dataset.price || 0), 0);
    }

    get payableSubtotalCents() {
      return this.subtotalCents - this.discountCents;
    }

    matchedTier(count) {
      return [...this.tiers].reverse().find((tier) => count >= tier.quantity) || null;
    }

    nextTier(count) {
      return this.tiers.find((tier) => tier.quantity > count) || null;
    }

    money(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(Number(cents || 0) / 100);
    }

    selectSize(size) {
      if (this.selectedInputs.some((input) => !input.variants.some((variant) => variant.size === size && variant.available))) {
        this.status.textContent = 'A selected scent is unavailable in this size. Remove it to switch.';
        this.classList.add('has-size-error');
        return;
      }
      this.classList.remove('has-size-error');
      this.size = size;
      this.render();
      if (this.inputs[0]) this.emitChange(this.inputs[0]);
    }

    selectCategory(button) {
      const category = button.dataset.apScentFilter;
      this.category = category;
      this.filters.forEach((filter) => {
        const active = filter === button;
        filter.setAttribute('aria-selected', active ? 'true' : 'false');
        filter.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-ap-scent-category]').forEach((card) => {
        card.hidden = card.dataset.apScentCategory !== category;
      });
      this.render();
      this.loadVisibleImages();
      if (this.rail) this.rail.scrollLeft = 0;
      requestAnimationFrame(() => this.renderScroll());
    }

    loadVisibleImages() {
      this.querySelectorAll('[data-ap-scent-category]:not([hidden]) img[data-src]').forEach((image) => {
        image.src = image.dataset.src;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.removeAttribute('data-src');
        image.removeAttribute('data-srcset');
      });
    }

    render() {
      const count = this.selectedOils.length;
      const packs = this.selectedInputs.length - count;
      const matched = this.matchedTier(count);
      const next = this.nextTier(count);
      this.querySelectorAll('[data-ap-scent-size]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.apScentSize === this.size)));
      this.inputs.forEach((input) => {
        const variant = input.variants.find((item) => item.size === this.size);
        const atLimit = input.dataset.pack !== 'true' && count >= this.maxSelected && !input.checked;
        input.disabled = atLimit || !variant?.available;
        const card = input.closest('.ap-df__scent');
        card?.classList.toggle('is-disabled', input.disabled);
        if (variant) {
          input.dataset.variantId = variant.id;
          input.dataset.price = variant.price;
          input.dataset.size = variant.size;
        }
        const price = card?.querySelector('[data-ap-scent-price]');
        card?.classList.toggle('has-compare', Boolean(variant && variant.compare > variant.price));
        if (price) price.innerHTML = variant ? `<span>${this.money(variant.price)}${variant.compare > variant.price ? ` <s>${this.money(variant.compare)}</s>` : ''}</span> <small>${input.dataset.pack === 'true' ? '3×' : ''}${this.size.replace('ml', ' ml')}</small>` : 'Unavailable';
        input.setAttribute('aria-label', `${input.dataset.name}, ${input.dataset.pack === 'true' ? '3 bottles of ' : ''}${this.size}${variant?.available ? '' : ', unavailable'}`);
      });
      this.classList.toggle('is-unlocked', Boolean(matched));
      const packTab = this.category === 'packs';
      if (this.deal) this.deal.textContent = packTab ? '3 scents per pack' : matched ? `${matched.percent}% off oils` : `${next.quantity} oils · ${next.percent}% off`;
      if (this.status) {
        if (packTab) this.status.textContent = 'Each pack includes 3 fragrances. Tap to select.';
        else if (!count) this.status.textContent = 'Pick 3 individual scents to unlock 10% off.';
        else if (!matched) this.status.textContent = `${count} selected · add ${next.quantity - count} more for ${next.percent}% off.`;
        else this.status.textContent = `${count} individual scents · ${matched.percent}% off oils.`;
      }
      if (this.selection) {
        this.selection.replaceChildren(...this.selectedInputs.map((input) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.removeScent = input.dataset.variantId;
          button.textContent = `${input.dataset.name}${input.dataset.pack === 'true' ? ' pack' : ''} ×`;
          button.setAttribute('aria-label', `Remove ${input.dataset.name}`);
          return button;
        }));
      }
      if (this.math) {
        const summary = [count ? `${count} oil${count === 1 ? '' : 's'}` : '', packs ? `${packs} pack${packs === 1 ? '' : 's'}` : ''].filter(Boolean).join(' + ');
        this.math.innerHTML = `<span>${summary ? `${summary} · ${this.size.replace('ml', ' ml')} each` : 'Scent total'}</span><strong>${this.money(this.payableSubtotalCents)}</strong>`;
        this.math.title = matched ? `Save ${this.money(this.discountCents)} on individual oils` : '';
      }
    }

    renderScroll() {
      if (!this.rail || !this.thumb) return;
      const track = this.thumb.parentElement;
      const cue = track.closest('.ap-df__scents-scroll-cue');
      const maxScroll = this.rail.scrollWidth - this.rail.clientWidth;
      if (maxScroll <= 0) {
        if (cue) cue.hidden = true;
        else track.hidden = true;
        return;
      }
      if (cue) cue.hidden = false;
      track.hidden = false;
      const thumbWidth = Math.max(44, track.clientWidth * (this.rail.clientWidth / this.rail.scrollWidth));
      const travel = track.clientWidth - thumbWidth;
      this.thumb.style.width = `${thumbWidth}px`;
      this.thumb.style.transform = `translateX(${travel * (this.rail.scrollLeft / maxScroll)}px)`;
    }

    emitChange(input) {
      this.dispatchEvent(new CustomEvent('ap:scent-picker-change', {
        bubbles: true,
        detail: {
          variantId: Number(input.dataset.variantId),
          name: input.dataset.name,
          selected: input.checked,
          count: this.selectedInputs.length,
          discountPercent: this.discountPercent,
        },
      }));
    }
  }

  customElements.define('ap-scent-upsell-picker', APScentUpsellPicker);
})();
